/**
 * Preview-only mock data. Loaded ONLY when `?demo=1` is on the URL
 * (guarded against jobhacki.com production by lib/auth.js) — used to
 * give a usable visual of the dashboard tools without a real session.
 *
 * Never imported on the marketing/landing surface — used only inside
 * /dashboard/* pages.
 */

const COMPANIES = [
  ["Boeing", "boeing.com"],
  ["Acehardware", "acehardware.com"],
  ["SmiInc", "smiinc.com"],
  ["FLG Partners", "flgpartners.com"],
  ["Stripe", "stripe.com"],
  ["Anthropic", "anthropic.com"],
  ["Notion", "notion.so"],
  ["Linear", "linear.app"],
  ["Vercel", "vercel.com"],
  ["Cloudflare", "cloudflare.com"],
];

const TITLES = [
  ["Janitor", "other", "Mid"],
  ["Senior Human Resources Business Partner", "hr-recruiting", "Senior"],
  ["Mailroom Scanner (Temporary)", "other", "Mid"],
  ["Membership Services Representative", "customer-support", "Entry"],
  ["Staff Software Engineer", "software-engineering", "Senior"],
  ["Product Designer", "product-design", "Mid"],
  ["Account Executive", "sales", "Mid"],
  ["Customer Success Manager", "customer-success", "Mid"],
  ["Data Scientist", "data-analytics", "Senior"],
  ["Frontend Engineer", "software-engineering", "Mid"],
  ["Recruiter", "hr-recruiting", "Entry"],
  ["Finance Manager", "finance-accounting", "Senior"],
];

const LOCATIONS = [
  ["USA - Ridley Park, PA", false],
  ["IL - Oak Brook", false],
  ["GA HQ", false],
  ["Busselton, WA", false],
  ["Remote · US", true],
  ["New York, NY", false],
  ["San Francisco, CA", false],
  ["Remote · Worldwide", true],
  ["London, UK", false],
  ["Berlin, DE", false],
];

const ATS = ["Workday", "Greenhouse", "Lever", "Ashby", "SmartRecruiters"];

function pick(arr, i) {
  return arr[i % arr.length];
}

export function demoJobs({
  page = 1,
  perPage = 20,
  q = "",
  level = "",
  type = "",
  remote = "",
  category = "",
  total = 338812,
} = {}) {
  const start = (page - 1) * perPage;
  const results = [];
  for (let i = 0; i < perPage; i++) {
    const seed = start + i;
    const [title, cat, lvl] = pick(TITLES, seed);
    const [comp, domain] = pick(COMPANIES, seed * 3 + 1);
    const [loc, isRemote] = pick(LOCATIONS, seed * 7 + 2);
    const ats = pick(ATS, seed * 5 + 3);
    if (q && !title.toLowerCase().includes(q.toLowerCase()) && !comp.toLowerCase().includes(q.toLowerCase())) continue;
    if (level && lvl.toLowerCase() !== level.toLowerCase()) continue;
    if (remote === "remote" && !isRemote) continue;
    if (type === "remote" && !isRemote) continue;
    if (category && cat !== category) continue;
    results.push({
      title,
      company: comp,
      companyDomain: domain,
      category: cat,
      level: lvl,
      location: loc,
      remote: isRemote,
      posted: seed < 4 ? "Today" : seed < 10 ? "1d" : `${Math.min(30, Math.ceil(seed / 3))}d`,
      posted_at: new Date(
        Date.now() - (seed < 4 ? 6 * 36e5 : seed < 10 ? 28 * 36e5 : Math.min(30, Math.ceil(seed / 3)) * 86_400_000)
      ).toISOString(),
      ats,
      new: seed < 6,
      url: `https://jobhacki.com/jobs/redirect?seed=${seed}`,
    });
  }
  return {
    ok: true,
    demo: true,
    results,
    total,
    page,
    pages: Math.max(1, Math.ceil(total / perPage)),
    facets: {
      // Mirror real API shape: facets.categories is a {slug: count} map.
      categories: {
        other: 80049,
        engineering: 27647,
        "software-engineering": 15351,
        healthcare: 23087,
        sales: 22365,
        consulting: 7074,
        retail: 13676,
        "hospitality-food": 12584,
        nursing: 11217,
        "customer-support": 6589,
        marketing: 8552,
        "finance-accounting": 7218,
        "project-management": 5879,
      },
    },
  };
}

export function demoMatches(resume = "") {
  const base = resume.toLowerCase();
  const seedScore = (s) => 60 + ((base.length + s.length) % 38);
  return {
    ok: true,
    demo: true,
    matches: TITLES.slice(0, 6).map(([title, cat, lvl], i) => {
      const [comp, domain] = COMPANIES[i % COMPANIES.length];
      const [loc] = LOCATIONS[i % LOCATIONS.length];
      const score = seedScore(title + comp);
      return {
        score,
        title,
        company: comp,
        companyDomain: domain,
        location: loc,
        category: cat,
        level: lvl,
        reason:
          score > 85
            ? "Strong overlap on listed tooling, level, and recent shipped work."
            : score > 70
              ? "Good fit on category & level — a few keywords missing."
              : "Adjacent fit — your background needs to lean into the missing skills.",
        apply_url: `https://jobhacki.com/jobs/redirect?demo=${i}`,
      };
    }),
    meta: { took_ms: 642, source: "demo" },
  };
}

export function demoRoleFit(role) {
  return {
    ok: true,
    demo: true,
    result: {
      role: role || "frontend-engineer",
      score: 78,
      level: 4,
      xp: 1240,
      strong: ["React", "Component architecture", "Accessibility", "Testing"],
      weak: ["System design at scale", "Performance budgets"],
      missing: ["Rust / WASM", "Webpack module federation", "Edge caching strategy"],
    },
  };
}

export const DEMO_ROLES = [
  { slug: "frontend-engineer", label: "Frontend Engineer", category: "Software Engineering" },
  { slug: "backend-engineer", label: "Backend Engineer", category: "Software Engineering" },
  { slug: "product-designer", label: "Product Designer", category: "Design" },
  { slug: "product-manager", label: "Product Manager", category: "Product" },
  { slug: "data-scientist", label: "Data Scientist", category: "Software Engineering" },
  { slug: "account-executive", label: "Account Executive", category: "Sales" },
  { slug: "customer-success-manager", label: "Customer Success Manager", category: "Customer Support" },
  { slug: "recruiter", label: "Recruiter", category: "HR" },
];

export const DEMO_PLAYBOOKS = [
  { slug: "ats-cheatcode", title: "The ATS Cheatcode", category: "Resume", blurb: "Format & language tricks that get you past the resume bots in 2026." },
  { slug: "cold-email-pipeline", title: "Cold Email Pipeline", category: "Outreach", blurb: "Build a 50-recruiter outreach pipeline in an afternoon." },
  { slug: "linkedin-magnet", title: "LinkedIn Magnet", category: "Personal Brand", blurb: "Turn your profile into an inbound engine." },
  { slug: "interview-deconstructed", title: "Interview Deconstructed", category: "Interview", blurb: "Every loop type, every signal recruiters score, every counter-question." },
  { slug: "salary-leverage", title: "Salary Leverage", category: "Negotiation", blurb: "Scripts to add $10K–$30K to your offer without burning the bridge." },
  { slug: "switch-careers", title: "Switch Careers in 90 Days", category: "Career Path", blurb: "Validated transitions, week-by-week plan." },
  { slug: "remote-roles-only", title: "Remote Roles Only", category: "Job Search", blurb: "Where remote-first companies actually post — and how to filter." },
  { slug: "first-90-days", title: "First 90 Days", category: "On the Job", blurb: "Land the offer; now keep it — the systems senior people use." },
];

export const DEMO_TOOLBOX = (() => {
  const cats = [
    ["Resume", "tool"],
    ["AI Prompt", "prompt"],
    ["Job Board", "tool"],
    ["GitHub Repo", "repo"],
    ["Outreach", "tool"],
    ["Interview", "tool"],
    ["Salary", "tool"],
    ["LinkedIn", "tool"],
  ];
  const names = [
    "Teal HQ", "Resumeworded", "Jobscan", "Huntr", "Simplify", "LazyApply",
    "Pyjamahr", "FlexJobs", "Wellfound", "Otta", "Levels.fyi", "Comparably",
    "BlindApp", "InterviewBit", "LeetCode", "Excalidraw", "Notion AI", "ChatGPT",
    "Claude", "Phind", "Perplexity", "Reclaim", "Calendly", "Clearbit",
    "Hunter.io", "Apollo.io", "Lemlist", "Mailshake", "Beautiful.ai", "Loom",
  ];
  const arr = [];
  for (let i = 0; i < 252; i++) {
    const [cat, kind] = cats[i % cats.length];
    const base = names[i % names.length];
    arr.push({
      slug: `${base.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${i}`,
      name: i < names.length ? base : `${base} ${Math.floor(i / names.length) + 1}`,
      category: cat,
      kind,
      blurb:
        kind === "prompt"
          ? "Battle-tested prompt — paste into your favorite model."
          : kind === "repo"
            ? "Open-source repo we lean on inside the playbooks."
            : "Pinned tool used inside the JobHacki playbooks.",
      url: `https://jobhacki.com/toolbox/${i}`,
    });
  }
  return arr;
})();
